import { addDays, addYears, format, isAfter, isBefore, subDays, subYears } from 'date-fns';

import {
  AdornedInput,
  Adornment,
  DatePicker,
  DatePickerInput,
  FormField,
  FormHelp,
  IconCalendarOutline,
  Label,
  Spacer,
  TextButton,
} from '@zillow/constellation';

import type { Lease, Section } from '../../../models/index';
import { LEASE_TERMS } from '../../../constants';
import LeaseTermsListBox from './LeaseTerms/LeaseTermsListBox';
import { DATE_PICKER_FORMAT, DATE_PICKER_PLACEHOLDER } from '../../../constants/stringConstants';
import { FULLSTORY_IGNORE_RAGE_CLICKS } from '../../../constants/analytics';
import { ValidationType } from '../../../utils/validators';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: Record<string, Date | string | boolean>) => void;
  lease: Lease;
};

const END_DATE_ERROR_MESSAGE = 'Enter a valid date that is after the start date';

function disableDatesOutsideRange(date: Date): boolean {
  const today = new Date();
  const sixYearsAgo = subYears(today, 6);
  const fiveYearsFromNow = addYears(today, 5);

  return isBefore(date, sixYearsAgo) || isAfter(date, fiveYearsFromNow);
}

export default function LeaseTerms_v4({ section, setSectionValues, lease }: Props): JSX.Element {
  const endDateValidator = (value: Date | string): boolean => {
    const startDate = section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE);
    const oneDayAfterStart = addDays(startDate, 1);

    if (startDate && value && isBefore(value, oneDayAfterStart)) {
      return false;
    }
    return true;
  };

  // Override the validators on every render to ensure they're always set correctly.
  // We have set the validator within the API to be "TermStartDateValidator" and "TermEndDateValidator", but since we don't want to mess with that, we're overriding it here.
  // This is necessary because SectionWrapper clones the section multiple times
  const startDateField = section.getField(LEASE_TERMS.FIELD_NAME.START_DATE);
  if (startDateField) {
    const currentValue = startDateField.enteredValue;
    startDateField.validation = ValidationType.TermsStartDateExtendedRangeValidator;
    if (currentValue) {
      startDateField.setEnteredValue(currentValue, true);
    }
  }

  const endDateField = section.getField(LEASE_TERMS.FIELD_NAME.END_DATE);
  if (endDateField) {
    const currentValue = endDateField.enteredValue;
    endDateField.validation = ValidationType.TermsStartDateExtendedRangeValidator;
    endDateField.customValidator = endDateValidator;
    if (currentValue) {
      endDateField.setEnteredValue(currentValue, true);
    }
  }

  function isSelected(termFieldName: string): boolean {
    return section.getFieldValue(termFieldName) || false;
  }

  function getStartDate(): string {
    return section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE);
  }

  function getEndDate(): string {
    return section.getFieldValue(LEASE_TERMS.FIELD_NAME.END_DATE);
  }

  // Start Date handlers
  function onStartDateChange(date: Date): void {
    setSectionValues({
      [LEASE_TERMS.FIELD_NAME.START_DATE]: date,
      [LEASE_TERMS.FIELD_NAME.END_DATE]: getEndDate(),
    });
  }

  // End Date handlers
  function onEndDateChange(date: Date): void {
    setSectionValues({
      [LEASE_TERMS.FIELD_NAME.END_DATE]: date,
    });
    validateEndDate();
  }

  function validateEndDate(): void {
    const field = section.getField(LEASE_TERMS.FIELD_NAME.END_DATE);
    field.setErrorMessage(END_DATE_ERROR_MESSAGE);
  }

  function onSetOneYearTermClick(): void {
    const startDate = getStartDate();
    const formattedOneYearLaterEndDate = format(subDays(addYears(startDate, 1), 1), 'MMMM D, YYYY');

    setSectionValues({ [LEASE_TERMS.FIELD_NAME.END_DATE]: formattedOneYearLaterEndDate });
  }

  function setOneYearTermLabel(): string {
    if (
      section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError ||
      !section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE)
    ) {
      return 'Set to 1 year term';
    }
    const startDate = getStartDate();
    const formattedOneYearLaterEndDate = format(subDays(addYears(startDate, 1), 1), 'MMMM D, YYYY');

    return `Set to 1 year term (${formattedOneYearLaterEndDate})`;
  }

  return (
    <>
      <FormField
        marginBottom="sm"
        label={<Label size="lg">What are your lease terms?</Label>}
        control={<LeaseTermsListBox section={section} setSectionValues={setSectionValues} lease={lease} />}
      />
      {(isSelected(LEASE_TERMS.FIELD_NAME.IS_MONTH_TO_MONTH) || isSelected(LEASE_TERMS.FIELD_NAME.IS_FIXED)) && (
        <Spacer marginBottom="sm">
          <FormField
            marginBottom="xs"
            label={<Label>{LEASE_TERMS.LABEL.START_DATE}</Label>}
            error={section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError}
            control={
              <AdornedInput
                className={FULLSTORY_IGNORE_RAGE_CLICKS}
                input={
                  <DatePickerInput
                    placeholder={DATE_PICKER_PLACEHOLDER}
                    value={getStartDate()}
                    onChange={onStartDateChange}
                    datepicker={<DatePicker dateFormat={DATE_PICKER_FORMAT} disabledDates={disableDatesOutsideRange} />}
                  />
                }
                rightAdornment={
                  <Adornment>
                    <IconCalendarOutline />
                  </Adornment>
                }
              />
            }
            description={
              section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError && (
                <FormHelp>{section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).getErrorMessage()}</FormHelp>
              )
            }
          />
          {!isSelected(LEASE_TERMS.FIELD_NAME.IS_MONTH_TO_MONTH) && (
            <Spacer marginTop="sm">
              <FormField
                marginBottom="xs"
                label={<Label>{LEASE_TERMS.LABEL.END_DATE}</Label>}
                error={section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).hasError}
                control={
                  <AdornedInput
                    className={FULLSTORY_IGNORE_RAGE_CLICKS}
                    input={
                      <DatePickerInput
                        placeholder={DATE_PICKER_PLACEHOLDER}
                        value={getEndDate()}
                        onChange={onEndDateChange}
                        datepicker={
                          <DatePicker dateFormat={DATE_PICKER_FORMAT} disabledDates={disableDatesOutsideRange} />
                        }
                      />
                    }
                    rightAdornment={
                      <Adornment>
                        <IconCalendarOutline />
                      </Adornment>
                    }
                  />
                }
                description={
                  section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).hasError && (
                    <FormHelp>{section.getField(LEASE_TERMS.FIELD_NAME.END_DATE).getErrorMessage()}</FormHelp>
                  )
                }
              />
              <TextButton
                disabled={
                  section.getField(LEASE_TERMS.FIELD_NAME.START_DATE).hasError ||
                  !section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE)
                }
                onClick={onSetOneYearTermClick}
              >
                {setOneYearTermLabel()}
              </TextButton>
            </Spacer>
          )}
        </Spacer>
      )}
    </>
  );
}
